import React, { ReactNode } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from "@mui/material";
import moment, { type Moment } from "moment";

import {
  IAttendanceSummaryRow,
  TAttendanceStatus
} from "../../../../interfaces/AttendanceInterface";
import * as calendarFns from "../../../../components/Calendar/functions/calendarFunctions";
import {
  toNepaliNumber,
  toNepaliDay
} from "../../../../components/Calendar/functions/calendarFunctions";
import { AttendanceStatus } from "../../../../constants/attendance";

const formatTime = (time: string | null) => {
  if (!time) return "-";
  return moment(time).format("hh:mm A");
};

const formatMinutes = (minutes: number) => {
  if (!minutes) return "0m";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const getStatusColor = (status: TAttendanceStatus) => {
  switch (status) {
    case AttendanceStatus.PRESENT:
      return { bg: "#E8F5E9", color: "#2E7D32" };
    case AttendanceStatus.ABSENT:
      return { bg: "#FFEBEE", color: "#D32F2F" };
    case AttendanceStatus.LATE:
      return { bg: "#FFF3E0", color: "#E65100" };
    case AttendanceStatus.LEAVE:
      return { bg: "#E3F2FD", color: "#1565C0" };
    case AttendanceStatus.HOLIDAY:
      return { bg: "#F3E5F5", color: "#7B1FA2" };
    case AttendanceStatus.WEEKLY_OFF:
      return { bg: "#F5F5F5", color: "#616161" };
    default:
      return { bg: "#F5F5F5", color: "#616161" };
  }
};

interface PropType {
  days: Moment[];
  attendances: IAttendanceSummaryRow[];
}

export default function AttendanceSummary({ days, attendances }: PropType): ReactNode {
  const getAttendanceForDay = (day: Moment) =>
    attendances.find((attendance) => moment(attendance.attendanceDate).isSame(day, "day"));

  const isToday = (day: Moment): boolean => day.isSame(moment(), "day");

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          "& .MuiTableCell-root": {
            borderColor: "divider",
            py: 1.5
          },
          maxHeight: "calc(100vh - 200px)"
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Day</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Check In</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Check Out</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Work Time</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {days.map((day) => {
              const attendance = getAttendanceForDay(day);
              const bsDate = calendarFns.convertADtoBS(day);
              const statusStyle = attendance ? getStatusColor(attendance.status) : null;

              return (
                <TableRow
                  key={day.toISOString()}
                  sx={{
                    backgroundColor:
                      attendance?.status === AttendanceStatus.WEEKLY_OFF ? "#FAFAFA" : "inherit"
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily:
                            "'Noto Sans Devanagari', 'Noto Sans', 'Noto Sans Display', 'Poppins', sans-serif"
                        }}
                      >
                        {toNepaliNumber(bsDate.bsDate)}
                      </Typography>

                      {isToday(day) && (
                        <Chip
                          label="Today"
                          size="small"
                          sx={{
                            backgroundColor: "rgba(76, 175, 80, 0.12)",
                            color: "rgba(76, 175, 80, 0.87)",
                            height: "20px",
                            fontSize: "0.75rem"
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontFamily:
                          "'Noto Sans Devanagari', 'Noto Sans', 'Noto Sans Display', 'Poppins', sans-serif",
                        fontSize: "0.875rem"
                      }}
                    >
                      {toNepaliDay(day.day())}
                    </Typography>
                  </TableCell>
                  <TableCell>{attendance ? formatTime(attendance.firstCheckIn) : "-"}</TableCell>
                  <TableCell>{attendance ? formatTime(attendance.lastCheckOut) : "-"}</TableCell>
                  <TableCell>
                    {attendance ? formatMinutes(attendance.workedMinutes) : "-"}
                  </TableCell>
                  <TableCell>
                    {attendance ? (
                      <Chip
                        label={attendance.status.replace("_", " ")}
                        size="small"
                        sx={{
                          backgroundColor: statusStyle?.bg,
                          color: statusStyle?.color,
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          textTransform: "capitalize"
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
